import {
  PlanLimitsService,
  SubscriptionService,
  UserPlanCount,
} from '../services'

const CONSUMED_ART_COUNT_LIMIT = 200

const getStorageToken = () => {
  if (typeof window === 'undefined') {
    return null
  }

  const rawToken = window.localStorage.getItem('token')

  if (!rawToken) {
    return null
  }

  try {
    return JSON.parse(rawToken)
  } catch {
    return rawToken
  }
}

const isAuthenticatedUser = () => !!getStorageToken()

const normalizeFirstItem = (data) => {
  if (Array.isArray(data)) {
    return data[0] ?? null
  }

  return data ?? null
}

const getFallbackRenewalDate = () => {
  const mesAtual = new Date(new Date().setDate(1))
  mesAtual.setDate(mesAtual.getDate() + 30)
  mesAtual.setHours(0, 0, 0, 0)
  return mesAtual
}

const isSameRenewalDay = (renewalDate, expirationPlan) =>
  renewalDate.getUTCFullYear() === expirationPlan.getUTCFullYear() &&
  renewalDate.getUTCMonth() === expirationPlan.getUTCMonth() &&
  renewalDate.getUTCDate() === expirationPlan.getUTCDate()

const withOptionalEventType = (payload, eventType) => {
  if (!eventType) {
    return payload
  }

  return {
    ...payload,
    eventType,
  }
}

const buildResetPayload = (renewalDate, artGenerationCount, eventType) =>
  withOptionalEventType(
    {
      ownScheduleCount: 0,
      postOnDesignCount: 0,
      imageBankCount: 0,
      aiImageCount: 0,
      aiLegendCount: 0,
      aiMessageCount: 0,
      artGenerationCount,
      renewalDate,
    },
    eventType
  )

const resolveSinglePostAvailability = (payload) =>
  typeof payload === 'boolean' ? payload : null

export const getEffectiveSinglePostLimit = (subscriptionFeatures) => {
  const features = subscriptionFeatures?.data ?? subscriptionFeatures ?? {}
  const isFreeOrNoPlan = Boolean(features.isFreeOrNoPlan)
  const rawSinglePostLimit = Number(features.singlePostLimit ?? 0)
  const singlePostLimit =
    isFreeOrNoPlan && rawSinglePostLimit === 0 ? 8 : rawSinglePostLimit
  const additionalSinglePostQuantity = Number(
    features.additionalSinglePostQuantity ?? 0
  )

  return singlePostLimit + additionalSinglePostQuantity
}

/** Snapshot for comparing art limit reads (used/total only). */
export const snapshotArtAvailability = (availability) => {
  if (!availability) {
    return null
  }

  const used = Number(availability.used)
  const total = Number(availability.total)

  if (!Number.isFinite(used) || !Number.isFinite(total)) {
    return null
  }

  return { used, total }
}

/**
 * Consumption only increases `used`; a lower `total` with stable `used` is unexpected
 * in the campaign chat flow (may indicate billing/cycle reconciliation or stale UI).
 */
export const warnIfArtLimitTotalDroppedWithoutUsageIncrease = (
  previous,
  nextAvailability,
  context = '',
) => {
  const next = snapshotArtAvailability(nextAvailability)

  if (!previous || !next) {
    return
  }

  if (next.total < previous.total && next.used <= previous.used) {
    console.warn('[art-limits] Total decreased without usage increase', {
      previous,
      next,
      context,
    })
  }
}

/**
 * On /ChatSocket, ChatSocketPage owns limit refresh and pushes state via syncArtLimits.
 * Skipping a parallel sidebar API fetch avoids stale reads overwriting fresh sync data.
 */
export function shouldSidebarRefreshArtLimitsOnPlanEvent({
  isChatSocketPage,
}) {
  return !isChatSocketPage
}

export const hasReachedSinglePostLimit = (
  artGenerationCount,
  subscriptionFeatures
) => {
  const effectiveLimit = getEffectiveSinglePostLimit(subscriptionFeatures)
  const used = Number(artGenerationCount ?? 0)

  return effectiveLimit > 0 && used >= effectiveLimit
}

const isSuccessfulServiceResponse = (response) =>
  [200, 201, 204].includes(Number(response?.responseStatus))

const getResponseStatus = (response) =>
  Number(response?.responseStatus ?? response?.response?.status ?? 0) || null

export const getArtGenerationAvailability = async (
  required = 1,
  options = {}
) => {
  const { allowCampaignGeneration = false } = options

  if (!isAuthenticatedUser()) {
    return {
      hasAvailableArts: true,
      available: Number.POSITIVE_INFINITY,
      total: Number.POSITIVE_INFINITY,
      used: 0,
    }
  }

  try {
    const [planLimitResponse, subscriptionFeatures] = await Promise.all([
      PlanLimitsService.checkSinglePostCountLimit(),
      SubscriptionService.getSubscriptionFeatures(),
    ])

    const total = getEffectiveSinglePostLimit(subscriptionFeatures?.data)
    const used = Number(subscriptionFeatures?.data?.artGenerationCount ?? 0)
    const available = Math.max(0, total - used)

    let hasAvailableArts = available >= required

    const hasSinglePostAvailability =
      planLimitResponse?.responseStatus === 200
        ? resolveSinglePostAvailability(planLimitResponse?.data)
        : null

    if (
      !allowCampaignGeneration &&
      required <= 1 &&
      hasSinglePostAvailability === false
    ) {
      hasAvailableArts = false
    }

    const result = {
      hasAvailableArts,
      available,
      total,
      used,
    }
    return result
  } catch (error) {
    console.error('Erro ao verificar disponibilidade de artes', error)

    return {
      hasAvailableArts: true,
      available: Number.POSITIVE_INFINITY,
      total: Number.POSITIVE_INFINITY,
      used: 0,
    }
  }
}

export const getCampaignAvailability = async (required = 1) => {
  if (!isAuthenticatedUser()) {
    return { hasAvailableCampaigns: true, available: Number.POSITIVE_INFINITY, total: Number.POSITIVE_INFINITY, used: 0 }
  }
  try {
    const subscriptionFeatures = await SubscriptionService.getSubscriptionFeatures()
    const total = Number(subscriptionFeatures?.data?.schedullingLimit ?? 0) + Number(subscriptionFeatures?.data?.additionalCampaignCount ?? 0)
    const used = Number(subscriptionFeatures?.data?.campaignCount ?? 0)
    const available = Math.max(0, total - used)
    return { hasAvailableCampaigns: available >= required, available, total, used }
  } catch (error) {
    console.error('Erro ao verificar disponibilidade de campanhas', error)
    return { hasAvailableCampaigns: true, available: Number.POSITIVE_INFINITY, total: Number.POSITIVE_INFINITY, used: 0 }
  }
}

export const getStrategyAvailability = async (required = 1) => {
  if (!isAuthenticatedUser()) {
    return { hasAvailableStrategies: true, available: Number.POSITIVE_INFINITY, total: Number.POSITIVE_INFINITY, used: 0 }
  }
  try {
    const subscriptionFeatures = await SubscriptionService.getSubscriptionFeatures()
    const total = Number(subscriptionFeatures?.data?.postLimit ?? 0) + Number(subscriptionFeatures?.data?.additionalStrategyCount ?? 0)
    const used = Number(subscriptionFeatures?.data?.strategyCount ?? 0)
    const available = Math.max(0, total - used)
    return { hasAvailableStrategies: available >= required, available, total, used }
  } catch (error) {
    console.error('Erro ao verificar disponibilidade de estratégias', error)
    return { hasAvailableStrategies: true, available: Number.POSITIVE_INFINITY, total: Number.POSITIVE_INFINITY, used: 0 }
  }
}

export const getAISuggestionAvailability = async (required = 1) => {
  if (!isAuthenticatedUser()) {
    return {
      hasAvailableArts: true,
      available: Number.POSITIVE_INFINITY,
      total: Number.POSITIVE_INFINITY,
      used: 0,
    }
  }

  try {
    const [planLimitResponse, subscriptionFeatures] = await Promise.all([
      PlanLimitsService.checkAISuggestionLimit(),
      SubscriptionService.getSubscriptionFeatures(),
    ])

    const total = Number(subscriptionFeatures?.data?.aiSuggestionLimit ?? 10)
    const used = Number(subscriptionFeatures?.data?.aiSuggestionCount ?? 0)
    const available = Math.max(0, total - used)

    let hasAvailableArts = available >= required

    const hasSinglePostAvailability =
      planLimitResponse?.responseStatus === 200
        ? resolveSinglePostAvailability(planLimitResponse?.data)
        : null

    if (
      required <= 1 &&
      hasSinglePostAvailability === false
    ) {
      hasAvailableArts = false
    }

    return {
      hasAvailableArts,
      available,
      total,
      used,
    }
  } catch (error) {
    console.error('Erro ao verificar disponibilidade de sugestões IA', error)

    return {
      hasAvailableArts: true,
      available: Number.POSITIVE_INFINITY,
      total: Number.POSITIVE_INFINITY,
      used: 0,
    }
  }
}

export const getGeneratedArtsCountFromResponse = (response) => {
  const responseData = response?.data?.data

  if (Array.isArray(responseData)) {
    return responseData.length
  }

  return responseData ? 1 : 0
}

export const mergeConsumedArtStatusIds = (existingIds = [], uuid) => {
  const nextIds = new Set(Array.isArray(existingIds) ? existingIds : [])

  if (uuid) {
    nextIds.add(uuid)
  }

  return Array.from(nextIds).slice(-CONSUMED_ART_COUNT_LIMIT)
}

export const consumeGeneratedArts = async (quantity = 1, options = {}) => {
  const normalizedQuantity = Number(quantity)

  if (
    !isAuthenticatedUser() ||
    !Number.isFinite(normalizedQuantity) ||
    normalizedQuantity <= 0
  ) {
    return {
      success: false,
      reason: 'invalid_quantity',
      quantity,
    }
  }

  const {eventType} = options

  try {
    const [userPlanCountResponse, userSubscriptionResponse] = await Promise.all([
      UserPlanCount.getUserPlanCountByUser(),
      SubscriptionService.getSubscriptionByUser(),
    ])

    const userPlanCountData = normalizeFirstItem(userPlanCountResponse?.data)
    const subscriptionData = normalizeFirstItem(userSubscriptionResponse?.data)
    const renewalDatePayload =
      subscriptionData?.expiration ?? getFallbackRenewalDate()

    if (!userPlanCountData) {
      const response = await UserPlanCount.postUserPlanCount(
        buildResetPayload(renewalDatePayload, normalizedQuantity, eventType)
      )

      if (isSuccessfulServiceResponse(response)) {
        return {
          success: true,
          operation: 'create',
          responseStatus: response.responseStatus,
          userPlanCountId: response?.data?.uuid ?? response?.data?.id ?? null,
        }
      }

      const responseStatus = getResponseStatus(response)
      console.error('Erro ao criar consumo de artes geradas', {
        quantity: normalizedQuantity,
        eventType,
        responseStatus,
      })

      return {
        success: false,
        operation: 'create',
        responseStatus,
        userPlanCountId: response?.data?.uuid ?? response?.data?.id ?? null,
      }
    }

    const renewalDate = new Date(userPlanCountData.renewalDate)
    const dataAtual = new Date()
    const expirationPlan = subscriptionData?.expiration
      ? new Date(subscriptionData.expiration)
      : new Date(renewalDatePayload)

    const userPlanCountId = userPlanCountData.uuid ?? userPlanCountData.id

    if (!userPlanCountId) {
      console.error('Erro ao consumir artes geradas: userPlanCount sem id', {
        quantity: normalizedQuantity,
        eventType,
      })

      return {
        success: false,
        reason: 'missing_user_plan_count_id',
        userPlanCountId: null,
      }
    }

    let response

    if (
      renewalDate.getTime() < dataAtual.getTime() ||
      !isSameRenewalDay(renewalDate, expirationPlan)
    ) {
      response = await UserPlanCount.putUserPlanCount(
        userPlanCountId,
        buildResetPayload(renewalDatePayload, normalizedQuantity, eventType)
      )
    } else {
      response = await UserPlanCount.putUserPlanCount(
        userPlanCountId,
        withOptionalEventType(
          {
            artGenerationCount:
              Number(
                userPlanCountData.artGenerationCount ??
                  userPlanCountData.singlePostCount ??
                  0
              ) + normalizedQuantity,
            renewalDate: userPlanCountData.renewalDate,
          },
          eventType
        )
      )
    }

    if (isSuccessfulServiceResponse(response)) {
      return {
        success: true,
        operation: 'update',
        responseStatus: response.responseStatus,
        userPlanCountId,
      }
    }

    const responseStatus = getResponseStatus(response)
    console.error('Erro ao atualizar consumo de artes geradas', {
      quantity: normalizedQuantity,
      eventType,
      userPlanCountId,
      responseStatus,
    })

    return {
      success: false,
      operation: 'update',
      responseStatus,
      userPlanCountId,
    }
  } catch (error) {
    console.error('Erro ao consumir artes geradas', {
      quantity: normalizedQuantity,
      eventType,
      error,
    })
    return {
      success: false,
      reason: 'exception',
      error,
    }
  }
}
